import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/flexkit/Command";
import { type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Popover, PopoverContent } from "@/components/podkit/popover/Popover";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import type React from "react";
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Textarea, type TextareaProps } from "@/components/podkit/forms/Textarea";

export type CommandSuggestion = {
    value: string;
    label: string;
    description?: string;
    id: string;
};

type Props = {
    suggestions?: CommandSuggestion[];
    onSuggestionSelect?: (suggestion: CommandSuggestion) => boolean;
    triggerChar?: string;
    popoverWidthOffset?: number;
    popoverSideOffset?: number;
    popoverSide?: "top" | "bottom";
    popoverAlignOffset?: number;
} & TextareaProps &
    PropsWithClassName;

const TextareaWithSuggestions = forwardRef<HTMLTextAreaElement, Props>(
    (
        {
            suggestions = [],
            onSuggestionSelect,
            triggerChar = "/",
            className,
            value,
            onChange,
            onKeyDown,
            onBlur,
            popoverWidthOffset = 16,
            popoverSideOffset = 24,
            popoverSide = "top",
            popoverAlignOffset,
            ...textareaProps
        },
        ref,
    ) => {
        const textareaRef = useRef<HTMLTextAreaElement>(null);
        useImperativeHandle(ref, () => textareaRef.current!, []);

        const commandRef = useRef<HTMLDivElement>(null);
        const [open, setOpen] = useState(false);
        const [filteredSuggestions, setFilteredSuggestions] = useState<CommandSuggestion[]>([]);
        const [selectedValue, setSelectedValue] = useState<string>("");
        const [currentCommand, setCurrentCommand] = useState<{
            query: string;
            startPos: number;
            endPos: number;
        } | null>(null);
        const [dropdownPosition, setDropdownPosition] = useState<{
            width: number;
        } | null>(null);
        const [announcementMessage, announce] = useState<string>("");

        const commandMenuId = useId();
        const getItemId = useCallback(
            (suggestion: CommandSuggestion) => {
                return `${commandMenuId}-item-${suggestion.id}`;
            },
            [commandMenuId],
        );

        // Extract current command being typed
        const getCurrentCommand = useCallback(
            (text: string, cursorPos: number) => {
                const beforeCursor = text.substring(0, cursorPos);
                const lastTriggerIndex = beforeCursor.lastIndexOf(triggerChar);

                if (lastTriggerIndex === -1) return null;

                // Check if trigger is at start of line or after whitespace
                const charBeforeTrigger = lastTriggerIndex > 0 ? beforeCursor[lastTriggerIndex - 1] : " ";
                if (charBeforeTrigger !== " " && charBeforeTrigger !== "\n") return null;

                // Extract query after trigger
                const query = beforeCursor.substring(lastTriggerIndex);

                // Check if there's a space after trigger (which would end the command)
                if (query.includes(" ", 1) || query.includes("\n", 1)) return null;

                return {
                    query,
                    startPos: lastTriggerIndex,
                    endPos: lastTriggerIndex + query.length,
                };
            },
            [triggerChar],
        );

        const updateSuggestions = useCallback(
            (text: string, cursorPos: number) => {
                const command = getCurrentCommand(text, cursorPos);
                setCurrentCommand(command);

                if (!command) {
                    setOpen(false);
                    setFilteredSuggestions([]);
                    announce("");
                    return;
                }

                let filtered: CommandSuggestion[];
                const searchQuery = command.query.substring(1).toLowerCase();

                if (searchQuery.length === 0) {
                    // Show all suggestions when just trigger is typed
                    filtered = suggestions;
                } else {
                    // Filter suggestions based on query
                    filtered = suggestions.filter(
                        (suggestion) =>
                            suggestion.value.toLowerCase().includes(searchQuery) ||
                            suggestion.label.toLowerCase().includes(searchQuery),
                    );
                }

                setFilteredSuggestions(filtered);

                if (filtered.length > 0) {
                    setOpen(true);

                    // Set initial selection
                    const initialSelection = filtered.find((s) => s.value === selectedValue) || filtered[0];
                    if (initialSelection.value !== selectedValue) {
                        setSelectedValue(initialSelection.value);
                    }

                    // Announce to screen readers with initial selection
                    announce(
                        `Command menu opened. ${filtered.length} command${filtered.length === 1 ? "" : "s"} available. Selected: ${initialSelection.value} ${initialSelection.label}: ${initialSelection.description || ""}`,
                    );
                } else {
                    setOpen(false);
                    announce("No commands found");
                }
            },
            [suggestions, getCurrentCommand, selectedValue],
        );

        const handleChange = useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = e.target.value;
                const cursorPos = e.target.selectionStart;

                onChange?.(e);
                updateSuggestions(newValue, cursorPos);
            },
            [onChange, updateSuggestions],
        );

        const handleSuggestionSelect = useCallback(
            (suggestionValue: string) => {
                const suggestion = filteredSuggestions.find((s) => s.value === suggestionValue);
                if (!suggestion || !textareaRef.current || !currentCommand) return;

                const textarea = textareaRef.current;
                const currentValue = textarea.value;

                let suggestionRealValue = suggestion.value;
                const dontInsertCommand = onSuggestionSelect?.(suggestion);
                if (dontInsertCommand) {
                    suggestionRealValue = "";
                }

                // Replace the current command with the selected suggestion
                const beforeCommand = currentValue.substring(0, currentCommand.startPos);
                const afterCommand = currentValue.substring(currentCommand.endPos);
                const newValue = beforeCommand + suggestionRealValue + " " + afterCommand;
                const newCursorPos = currentCommand.startPos + suggestion.value.length + 1;

                // Update textarea value
                textarea.value = newValue;
                textarea.setSelectionRange(newCursorPos, newCursorPos);

                // Create a proper change event
                const changeEvent = {
                    target: textarea,
                    currentTarget: textarea,
                } as React.ChangeEvent<HTMLTextAreaElement>;

                onChange?.(changeEvent);

                setOpen(false);
                setCurrentCommand(null);
                setFilteredSuggestions([]);
                setSelectedValue("");
                announce(`Command ${suggestion.label} inserted`);

                // Return focus to textarea
                textarea.focus();
            },
            [currentCommand, filteredSuggestions, onChange, onSuggestionSelect],
        );

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (open && filteredSuggestions.length > 0) {
                    switch (e.key) {
                        case "ArrowDown": {
                            e.preventDefault();
                            const currentIndex = filteredSuggestions.findIndex((s) => s.value === selectedValue);
                            const nextIndex = (currentIndex + 1) % filteredSuggestions.length;
                            const nextCommand = filteredSuggestions[nextIndex];
                            setSelectedValue(nextCommand.value);
                            announce(`${nextCommand.value} ${nextCommand.label}: ${nextCommand.description || ""}`);
                            return;
                        }
                        case "ArrowUp": {
                            e.preventDefault();
                            const currentIndex = filteredSuggestions.findIndex((s) => s.value === selectedValue);
                            const prevIndex = currentIndex === 0 ? filteredSuggestions.length - 1 : currentIndex - 1;
                            const prevCommand = filteredSuggestions[prevIndex];
                            setSelectedValue(prevCommand.value);
                            announce(`${prevCommand.value} ${prevCommand.label}: ${prevCommand.description || ""}`);
                            return;
                        }
                        case "Enter":
                        case "Tab": {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSuggestionSelect(selectedValue);
                            return;
                        }
                        case "Escape": {
                            e.preventDefault();
                            setOpen(false);
                            setCurrentCommand(null);
                            setFilteredSuggestions([]);
                            setSelectedValue("");
                            announce("Command menu closed");
                            return;
                        }
                        default:
                            break;
                    }
                }

                // Update suggestions on cursor movement
                if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                    setTimeout(() => {
                        const textarea = e.target as HTMLTextAreaElement;
                        updateSuggestions(textarea.value, textarea.selectionStart);
                    }, 0);
                }

                onKeyDown?.(e);
            },
            [open, filteredSuggestions, selectedValue, handleSuggestionSelect, updateSuggestions, onKeyDown],
        );

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLTextAreaElement>) => {
                const textarea = e.target as HTMLTextAreaElement;
                setTimeout(() => {
                    updateSuggestions(textarea.value, textarea.selectionStart);
                }, 0);
            },
            [updateSuggestions],
        );

        const handleBlur = useCallback(
            (e: React.FocusEvent<HTMLTextAreaElement>) => {
                // Check if focus is moving to the command menu
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (relatedTarget && commandRef.current?.contains(relatedTarget)) {
                    // Focus is moving to command menu, don't close
                    return;
                }

                // Delay dismissing suggestions to allow click events to complete
                setTimeout(() => {
                    setOpen(false);
                    setCurrentCommand(null);
                    setFilteredSuggestions([]);
                    setSelectedValue("");
                }, 150);
                onBlur?.(e);
            },
            [onBlur],
        );

        // Update dropdown position on resize
        useEffect(() => {
            if (!open || !textareaRef.current) return;

            const textarea = textareaRef.current;
            const resizeObserver = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    setDropdownPosition({ width: entry.borderBoxSize[0].inlineSize + popoverWidthOffset });
                }
            });

            resizeObserver.observe(textarea);

            return () => {
                resizeObserver.disconnect();
            };
        }, [open, popoverWidthOffset]);

        const activeDescendant = useMemo(() => {
            if (!open || !selectedValue) return undefined;
            const selected = filteredSuggestions.find((s) => s.value === selectedValue);

            return selected ? getItemId(selected) : undefined;
        }, [open, selectedValue, filteredSuggestions, getItemId]);

        useEffect(() => {
            if (!open || !selectedValue) return;

            requestAnimationFrame(() => {
                const selectedElement = document.querySelector(`[data-value="${selectedValue}"]`);
                if (selectedElement) {
                    selectedElement.scrollIntoView({
                        block: "nearest",
                        behavior: "auto",
                        inline: "nearest",
                    });
                }
            });
        }, [selectedValue, open]);

        return (
            <div className="relative">
                {/* mx-[1px] ensures there's a tiny bit of spacing between the scrollbar and the container */}
                <div role="combobox" aria-expanded={open} aria-haspopup="listbox" className="mx-[1px]">
                    <Textarea
                        ref={textareaRef}
                        className={className}
                        value={value}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onClick={handleClick}
                        onBlur={handleBlur}
                        aria-controls={commandMenuId}
                        aria-activedescendant={activeDescendant}
                        aria-autocomplete="list"
                        {...textareaProps}
                        data-tracking-id="suggestion-selected-textarea-with-suggestions"
                    />
                </div>

                {/* Screen reader announcements */}
                <div className="sr-only" role="status" aria-live="assertive" aria-atomic="true">
                    {announcementMessage}
                </div>

                {filteredSuggestions.length > 0 && (
                    <Popover open={open} modal={false}>
                        <PopoverPrimitive.Anchor asChild virtualRef={textareaRef} />
                        <PopoverContent
                            side={popoverSide}
                            sideOffset={popoverSideOffset}
                            align="start"
                            alignOffset={popoverAlignOffset}
                            className="m-0 border-0 p-0"
                            style={{
                                width: dropdownPosition?.width,
                            }}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                            <Command
                                id={commandMenuId}
                                className="size-full max-h-64"
                                value={selectedValue}
                                onValueChange={setSelectedValue}
                            >
                                <CommandList>
                                    <CommandEmpty>No commands found</CommandEmpty>
                                    <CommandGroup>
                                        {filteredSuggestions.map((suggestion) => (
                                            <CommandItem
                                                key={suggestion.value}
                                                id={getItemId(suggestion)}
                                                data-index={suggestion.id}
                                                value={suggestion.value}
                                                onSelect={handleSuggestionSelect}
                                                className="cursor-pointer"
                                            >
                                                <div className="flex w-full flex-col gap-0.5">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="flex-1 truncate font-medium">
                                                            {suggestion.label}
                                                        </span>
                                                        <span className="shrink-0 font-mono text-xs text-content-brand">
                                                            {suggestion.value}
                                                        </span>
                                                    </div>
                                                    {suggestion.description && (
                                                        <div className="truncate font-mono text-xs text-content-muted">
                                                            {suggestion.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        );
    },
);
TextareaWithSuggestions.displayName = "TextareaWithSuggestions";

export default TextareaWithSuggestions;
